// Khung form "Job mới" chép từ script board Mint-JobNew (common1 · seaBk · seaRoute · seaCargo · airBk · airCargo · lsSvc).
// Chỉ khai trường + luật bắt buộc; chưa ghi DB (D-05).

export type JobKind = 'sea' | 'air' | 'ls';
export type FieldType = 'text' | 'sel' | 'seg' | 'chk' | 'area';
export type FormValues = Readonly<Record<string, string>>;

export interface FormField {
  key: string;
  label: string;
  type: FieldType;
  /** số cột chiếm trên lưới 4 cột */
  span: number;
  /** `sel` / `seg`: danh sách lựa chọn · còn lại: gợi ý trong ô */
  opts?: readonly string[];
  hint?: string;
  required: boolean | ((v: FormValues) => boolean);
}
export interface FormSection {
  key: string;
  label: string;
  sub: string;
  fields: readonly FormField[];
}

const sel = (key: string, label: string, opts: readonly string[], required: FormField['required'] = false, span = 1): FormField => ({ key, label, type: 'sel', span, opts: opts.filter(Boolean), required });
const seg = (key: string, label: string, opts: readonly string[], required: FormField['required'] = false, span = 1): FormField => ({ key, label, type: 'seg', span, opts, required });
const text = (key: string, label: string, hint = '', required: FormField['required'] = false, span = 1): FormField => ({ key, label, type: 'text', span, hint, required });
const area = (key: string, label: string, hint = '', required: FormField['required'] = false, span = 1): FormField => ({ key, label, type: 'area', span, hint, required });

export const KIND_LABEL: Readonly<Record<JobKind, string>> = { sea: 'Đường biển', air: 'Hàng không', ls: 'Dịch vụ lẻ (LS)' };

export const START_OPTIONS: ReadonlyArray<{ key: string; label: string; tip: string }> = [
  { key: 'blank', label: 'Trống', tip: 'Nhập tay từ đầu' },
  { key: 'quote', label: 'Từ báo giá', tip: 'Điền theo báo giá khách đã chấp nhận' },
  { key: 'copy', label: 'Copy booking', tip: 'Sao chép từ một job có sẵn' },
  { key: 'ai', label: 'Hana đọc nội dung', tip: 'Dán mail / booking / clipboard để Hana điền' },
  { key: 'chat', label: 'Từ chat Hana', tip: 'Tin nhắn chat có yêu cầu đặt chỗ' },
  { key: 'multi', label: 'Multi Book', tip: 'Nhiều booking từ lịch tàu' },
];

export const CUS = ['SAVITA', 'COCOGROUP', 'GREENBUD', 'MKAGRISEA', 'QUEKYCORP', 'GNP IMEX', 'TAMNGOC', 'NEWWIND', 'THACO'];
export const PORT = ['VNSGN · Hồ Chí Minh', 'VNVUT · Vũng Tàu', 'VNHPH · Hải Phòng', 'KRPUS · Busan', 'KRINC · Incheon', 'CNNSA · Nansha', 'JPTYO · Tokyo', 'JPYOK · Yokohama', 'HKHKG · Hong Kong', 'SGSIN · Singapore', 'USNYC · New York', 'USLAX · Los Angeles', 'SAJED · Jeddah', 'ITGOA · Genoa'];
const APT = ['SGN · Tân Sơn Nhất', 'HAN · Nội Bài', 'NRT · Narita', 'FRA · Frankfurt', 'SZX · Thâm Quyến', 'DOH · Doha'];
const CARRIER = ['ONE', 'COS', 'WHL', 'HMM', 'KMT', 'EMC', 'IAL', 'CNC', 'YML', 'HAL', 'MSC', 'SNL', 'MSK', 'PIL', 'ANL', 'OOL', 'SKR'];
const TERMINAL = ['CAT LAI TMNL, VN', 'TCIT - Cảng Quốc tế Tân Cảng Cái Mép', 'GEMALINK', 'CANG SP-ITC', 'Tan Cang Cai Mep Thi Vai', 'Cảng Phước An'];
const CARGO = ['DC · hàng khô', 'RF · hàng lạnh', 'OTH · khác'];
const isRF = (v: FormValues) => v.tcargo === 'RF · hàng lạnh';
const FCL = (v: FormValues) => v.tship !== 'LCL';

const customer: FormSection = {
  key: 'cus',
  label: 'Khách & phụ trách',
  sub: 'Sale, CS, Sale Asst, Kế toán theo khách · BKG nhận xử lý sau',
  fields: [
    sel('cus', 'Khách hàng', CUS, true, 2),
    text('shipper', 'Shipper', 'Tên shipper in trên B/L', false, 2),
    sel('sale', 'Sales', ['DAVID', 'PHILIP', 'TUANNT', 'HAN', 'ANDG'], true),
    sel('sa', 'Sales Assistant', ['NHINTT', 'AnNPH']),
    sel('cs', 'CS', ['HOATTN', 'MAITTN', 'ThuyNTT', 'ThuongLVH'], true),
    sel('acc', 'Kế toán (NameAcc)', ['LIENNTK', 'DuyenTM', 'NGOCANH']),
    sel('bkg', 'BKG nhận xử lý', ['PhuongHT', 'PHUONGNKT', 'NhiTHT', 'TranTPB']),
    text('cref', 'CustomerReference', 'PO / Ref của khách', false, 2),
  ],
};

const SEA: readonly FormSection[] = [
  customer,
  {
    key: 'bk',
    label: 'Booking',
    sub: 'kênh, hãng, hợp đồng cước, trạng thái',
    fields: [
      sel('chan', 'Kênh booking', ['Spot', 'Official', 'Offline', 'Online', 'Co-Loader', 'Nominate'], true),
      sel('carrier', 'Hãng tàu', CARRIER, true),
      { ...text('colo', 'Co-loader', 'Chỉ khi kênh Co-Loader', (v) => v.chan === 'Co-Loader') },
      text('contract', 'SC number · hợp đồng cước', 'VD: SC26-ONE-0142 · trống nếu không có'),
      seg('bstatus', 'Trạng thái lúc tạo', ['RQ Request', 'BK Standby', 'BK Confirmed'], true, 2),
      seg('costterm', 'Cost payment', ['Prepaid', 'Collect']),
      text('nbk', 'Số booking (NumberofBkg)', '1'),
      text('bkno', 'BookingNo', 'Có thể nhập sau', (v) => v.bstatus === 'BK Confirmed', 2),
    ],
  },
  {
    key: 'route',
    label: 'Lịch trình',
    sub: 'cảng, tàu, cut-off, terminal',
    fields: [
      sel('pol', 'POL', PORT, true),
      sel('pod', 'POD', PORT, true),
      { key: 'haspot', label: 'Chuyển tải', type: 'chk', span: 1, hint: 'Có cảng chuyển tải', required: false },
      sel('pot', 'POT', PORT, (v) => v.haspot === '1'),
      text('vsl', 'Tàu / chuyến', 'Chọn từ lịch tàu hoặc gõ', false, 2),
      text('etd', 'ETD', 'dd/mm/yyyy', true),
      text('eta', 'ETA', 'dd/mm/yyyy'),
      text('closecy', 'Cut-off CY', 'dd/mm/yyyy hh:mm'),
      sel('tml', 'Working terminal', TERMINAL),
      text('icd', 'Nơi trả hàng / ICD', 'DP250039 DEPOT TÂN CẢNG SUỐI TIÊN'),
      seg('tmode', 'Transport mode', ['Port', 'ICD', 'Door']),
      seg('stuff', 'Nơi đóng hàng (StuffingPlace)', ['WH', 'CY']),
      seg('contseal', 'Điều kiện', ['FCL/FCL - CY/CY', 'LCL/LCL - CFS/CFS']),
      text('stplace', 'Địa chỉ đóng hàng', 'Kho khách'),
    ],
  },
  {
    key: 'cargo',
    label: 'Hàng hoá',
    sub: 'loại lô, container, commodity',
    fields: [
      seg('tship', 'Loại lô', ['FCL', 'LCL'], true),
      seg('tcargo', 'TypeCargo', CARGO, true, 2),
      sel('tcont', 'Loại cont', ['20DC', '40DC', '40HC', '20RF', '40RF', '40RH', '20RH', '40FR'], FCL),
      text('vol', 'Số cont', '1', FCL),
      text('cbm', 'CBM', 'LCL'),
      text('pcs', 'Số kiện'),
      sel('pkg', 'Đơn vị kiện', ['CARTON(S)', 'BAG(S)', 'PALLET', 'PACKAGE', 'BALE(S)', 'ROLL', 'BUNDLE']),
      text('comm', 'Commodity', 'Frozen Shrimp, COIR ROPE…', true, 2),
      text('contno', 'Số container (nếu đã có)', 'VD: WHLU4012345'),
      text('temp', 'TempRF (°C)', 'Chỉ hàng lạnh', isRF),
      text('vent', 'Ventilation', 'Chỉ hàng lạnh'),
      area('rmk', 'REMARKBK · ghi chú booking', 'Gửi hãng cùng booking request', false, 2),
      area('frmk', 'FreightRemark'),
      area('irmk', 'InternalRemark', 'Không gửi khách / hãng'),
    ],
  },
];

const AIR: readonly FormSection[] = [
  customer,
  {
    key: 'bk',
    label: 'Booking chuyến bay',
    sub: 'chuyến bay, sân bay, chặng nối',
    fields: [
      sel('airl', 'Hãng bay', ['VN', 'LH', 'CZ', 'QR', 'EK'], true),
      text('flt', 'FlightNo', 'VN300', true),
      text('etd', 'Ngày bay', 'dd/mm/yyyy', true),
      text('eta', 'Ngày đến', 'dd/mm/yyyy'),
      sel('pol', 'Sân bay đi', APT, true),
      sel('pod', 'Sân bay đến', APT, true),
      sel('via', 'Via', APT),
      text('leg', 'Chặng nối', 'VN300 / LH8417'),
      sel('atml', 'AirTml', ['TCS', 'SCSC', 'ALSC', 'NCTS']),
      sel('chan', 'Kênh booking', ['Spot', 'Official', 'Co-Loader'], true),
      text('bkno', 'Booking / MAWB', 'Có thể nhập sau', false, 2),
    ],
  },
  {
    key: 'cargo',
    label: 'Hàng hoá',
    sub: 'kiện, GW / CW, CBM',
    fields: [
      text('pcs', 'Pieces', '', true),
      sel('pkg', 'Đơn vị kiện', ['CARTON(S)', 'PACKAGE', 'PALLET']),
      text('gw', 'AirGW (kg)', '', true),
      text('cw', 'AirCW (kg)', 'Tự tính theo CBM'),
      text('cbm', 'CBM'),
      seg('tcargo', 'TypeCargo', CARGO, true, 2),
      text('comm', 'Commodity', 'Mô tả hàng', true),
      area('rmk', 'Ghi chú booking', '', false, 2),
      area('irmk', 'Ghi chú nội bộ', '', false, 2),
    ],
  },
];

const LS: readonly FormSection[] = [
  customer,
  {
    key: 'svc',
    label: 'Dịch vụ lẻ',
    sub: 'job không có cước chính',
    fields: [
      seg('lsmode', 'Dịch vụ', ['CCF · Hải quan', 'COO · C/O', 'FUM · Hun trùng', 'PTF · Kiểm dịch', 'TRF · Vận tải', 'SCY · Đóng hàng tại bãi'], true, 4),
      text('bkno', 'Booking / B/L tham chiếu', 'Lô hàng của khách'),
      sel('carrier', 'Hãng tàu', CARRIER),
      text('vsl', 'Tàu'),
      text('voy', 'Chuyến'),
      sel('pol', 'POL', PORT),
      sel('pod', 'POD', PORT),
      sel('wtml', 'WorkingTerminal', TERMINAL.slice(0, 4)),
      text('wcy', 'WorkingCloseCY', 'dd/mm/yyyy hh:mm'),
      text('tax', 'Taxcode người khai', 'Nếu khác khách'),
      sel('tcont', 'Loại cont', ['20DC', '40HC', '20RF', '40RH']),
      text('vol', 'Số cont'),
      seg('tcargo', 'TypeCargo', ['DC', 'RF', 'OTH']),
      area('rmk', 'Yêu cầu của khách', 'Nội dung dịch vụ, địa điểm, thời hạn', true, 4),
    ],
  },
];

export function sectionsFor(kind: JobKind): readonly FormSection[] {
  return kind === 'air' ? AIR : kind === 'ls' ? LS : SEA;
}

const isReq = (f: FormField, v: FormValues) => (typeof f.required === 'function' ? f.required(v) : f.required);

/** Trường bắt buộc đang trống (theo thứ tự trên form). */
export function missingFields(kind: JobKind, v: FormValues): FormField[] {
  return sectionsFor(kind).flatMap((s) => s.fields.filter((f) => isReq(f, v) && !(v[f.key] ?? '').trim()));
}
export const isRequired = isReq;
